/**
 * UnifiedFAB — Speed Dial a Helpdesk, Chat és pRI drawer-ekhez.
 * Egy gomb → kattintásra felfele nyílnak a lehetőségek → drawer nyílik.
 */
import { useChatStore } from '@/stores/chat-store';
import { useTranslation } from '@kgc/i18n';
import { useBreakpoint } from '@kgc/ui';
import { memo, useEffect } from 'react';
import { ChatWindow } from '../chat/ChatWindow';
import { HelpdeskDrawer } from '../helpdesk/HelpdeskDrawer';
import { useHelpdeskStore } from '../helpdesk/helpdesk-store';
import { HELPDESK_CONFIG } from '../helpdesk/helpdesk.config';
import { consoleCapture } from '../helpdesk/lib/console-capture';
import { BOTTOM_NAV_HEIGHT } from '../layout/nav-config';
import { PriDrawer } from '../pri';
import type { DrawerType } from './unified-fab-store';
import { useUnifiedFabStore } from './unified-fab-store';

// ── Speed Dial Item Config ──

interface FabItem {
  id: DrawerType;
  icon: string;
  label: string;
  enabled: boolean;
  disabledLabel?: string;
}

// ⚠️ A lista a KOMPONENSEN BELÜL épül, nem modul-szinten: a `label` `t()`-ből jön,
// és a `t` csak hookból érhető el. A kulcsok SZÁNDÉKOSAN statikus literálok —
// az `i18n-key-guard.spec` csak azokat látja; egy `t(item.labelKey)` alak dinamikus
// hívás lenne, és a merge-blokkoló 4-nyelv-ellenőrzés némán kimaradna.

// ── Main Component ──

function UnifiedFABInner() {
  const { t } = useTranslation();
  const { isMobile } = useBreakpoint();

  const fabItems: FabItem[] = [
    { id: 'pri', icon: '🤖', label: t('unifiedFab.items.pri'), enabled: true },
    { id: 'chat', icon: '💬', label: t('unifiedFab.items.chat'), enabled: true },
    { id: 'helpdesk', icon: '🐛', label: t('unifiedFab.items.helpdesk'), enabled: true },
  ];

  const {
    menuOpen,
    activeDrawer,
    badges,
    toggleMenu,
    closeMenu,
    openDrawer,
    closeDrawer,
    setBadge,
  } = useUnifiedFabStore();

  // Helpdesk store szinkron (drawer open/close)
  const helpdeskStore = useHelpdeskStore();

  // A Helpdesk-belépő kapcsolója (🔵-döntés 2026-08-11: a Google-integráció
  // halasztva, a kulcs törlésre megy → kredencial nélkül a bejelentés nyom nélkül
  // elvész). A flag eddig is létezett, de csak a sehol nem renderelt
  // `HelpdeskButton`-ban volt fogyasztva — itt, az élő belépőn nem.
  // ⚠️ Build-időben rögzített, kliens-oldali kapcsoló: NEM per-tenant és nem
  // környezet-szintű (a BE `GET /helpdesk/config` nem ad vissza `enabled` mezőt),
  // tehát egy `false` egy build MINDEN telepítésére vonatkozik.
  const helpdeskEnabled = HELPDESK_CONFIG.enabled;
  const visibleItems = fabItems.filter(item => item.id !== 'helpdesk' || helpdeskEnabled);

  // Chat store szinkron (unread count)
  const chatStore = useChatStore();

  // Badge frissítés: helpdesk (console errors) — elrejtett belépőnél el sem indul
  useEffect(() => {
    if (!helpdeskEnabled) return;
    const interval = setInterval(() => {
      setBadge('helpdesk', consoleCapture.getErrorCount());
    }, 5000);
    return () => clearInterval(interval);
  }, [helpdeskEnabled, setBadge]);

  // Badge frissítés: chat (unread count)
  useEffect(() => {
    const unread = chatStore.chats.reduce((sum, chat) => sum + chat.unreadCount, 0);
    setBadge('chat', unread);
  }, [chatStore.chats, setBadge]);

  // Keyboard shortcut: Ctrl+Shift+H (helpdesk), Escape (close)
  useEffect(() => {
    function handleKey(e: KeyboardEvent) {
      // ⚠️ A guard az ÁGON BELÜL van, NEM a handler elején: a korai `return`
      // az alatta lévő Escape-kezelést is kivenné (az egy őr, két különböző
      // dolgot kapuz). Regresszió-őre: a spec két Escape-esete.
      if (helpdeskEnabled && e.ctrlKey && e.shiftKey && e.key === 'H') {
        e.preventDefault();
        if (activeDrawer === 'helpdesk') {
          closeDrawer();
        } else {
          openDrawer('helpdesk');
        }
      }
      if (e.key === 'Escape') {
        if (activeDrawer) closeDrawer();
        else if (menuOpen) closeMenu();
      }
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [helpdeskEnabled, activeDrawer, menuOpen, openDrawer, closeDrawer, closeMenu]);

  // Drawer open handler — szinkronba hozza a helpdesk store-t
  function handleOpenDrawer(id: DrawerType) {
    if (!visibleItems.find(i => i.id === id)?.enabled) return;
    openDrawer(id);
    if (id === 'helpdesk') {
      helpdeskStore.open();
    } else if (id === 'chat') {
      chatStore.openChat();
    }
  }

  // Drawer close handler
  function handleCloseDrawer() {
    if (activeDrawer === 'helpdesk') helpdeskStore.close();
    if (activeDrawer === 'chat') chatStore.closeChat();
    closeDrawer();
  }

  // Összesített badge
  const totalBadge = badges.helpdesk + badges.chat + badges.pri;

  const bottomOffset = isMobile ? BOTTOM_NAV_HEIGHT + 8 : 24;

  // Ha drawer aktív → csak a drawer-t rendereljük
  // Fail-closed: elrejtett belépőnél a `helpdesk` állapot sem renderel drawert
  // (a store-t elvileg semmi nem tudja odaállítani — ez a második réteg).
  if (activeDrawer === 'helpdesk' && helpdeskEnabled) {
    return <HelpdeskDrawer onClose={handleCloseDrawer} />;
  }

  if (activeDrawer === 'chat') {
    return (
      <div className="fixed right-4 z-[60]" style={{ bottom: bottomOffset }}>
        <ChatWindow />
      </div>
    );
  }

  if (activeDrawer === 'pri') {
    return <PriDrawer onClose={handleCloseDrawer} />;
  }

  return (
    <>
      {/* Backdrop — menu nyitva */}
      {menuOpen && <div className="fixed inset-0 z-[54]" onClick={closeMenu} />}

      <div className="fixed right-4 z-[55]" style={{ bottom: bottomOffset }}>
        {/* Speed Dial Menu — itemek felfelé */}
        {menuOpen && (
          <div className="absolute bottom-16 right-0 flex flex-col-reverse gap-3 pb-2">
            {visibleItems.map((item, idx) => (
              <button
                key={item.id}
                // A horgony `data-testid`, NEM a `label` szövege (🚦-kikötés ③): a
                // label mostantól `t()`-ből jön, tehát a szöveg-horgony a nyelv-váltással
                // elmozdulna. A `testid` a fordítástól FÜGGETLEN ⇒ a defektet OSZTÁLYKÉNT
                // zárja, nem példányként — ugyanaz a család, ami a `#1275`-on 12 tesztet
                // némított el.
                data-testid={`fab-item-${item.id}`}
                onClick={() => handleOpenDrawer(item.id)}
                disabled={!item.enabled}
                className={`flex items-center gap-3 whitespace-nowrap transition-all ${
                  item.enabled ? 'opacity-100 hover:scale-105' : 'cursor-not-allowed opacity-50'
                }`}
                style={{
                  animation: `fab-item-in 0.2s ease-out ${idx * 50}ms both`,
                }}
              >
                {/* Label pill */}
                <span className="rounded-lg bg-slate-800 px-3 py-1.5 text-xs font-medium text-slate-200 shadow-lg">
                  {item.enabled ? item.label : item.disabledLabel || item.label}
                </span>

                {/* Icon button */}
                <span className="relative flex h-12 w-12 items-center justify-center rounded-full bg-slate-700 text-lg shadow-lg transition-colors hover:bg-slate-600">
                  {item.icon}
                  {/* Badge */}
                  {badges[item.id] > 0 && (
                    <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
                      {badges[item.id] > 9 ? '9+' : badges[item.id]}
                    </span>
                  )}
                </span>
              </button>
            ))}
          </div>
        )}

        {/* Fő FAB gomb */}
        <button
          onClick={toggleMenu}
          // A `title` a flag állásától FÜGG (a shortcut csak bekapcsolt Helpdesknél él),
          // ezért E2E-horgonynak alkalmatlan: a `pri-drawer.e2e.ts` szó szerinti
          // `button[title="…(Ctrl+Shift+H)"]` szelektora a `false` ágon nem talált, és a
          // fájl mind a 12 tesztje NÉMÁN skippelt (`openPriDrawer()` → `false`).
          // A `data-testid` a flag MINDKÉT állásában azonos ⇒ a horgony stabil.
          data-testid="unified-fab-button"
          className="relative flex h-14 w-14 items-center justify-center rounded-full bg-slate-700/90 text-xl shadow-xl backdrop-blur-sm transition-all hover:bg-slate-600 hover:shadow-2xl active:scale-95"
          // A billentyű-kombó (`Ctrl+Shift+H`) SZÁNDÉKOSAN nem fordul egyik locale-ban
          // sem: a `handleKey` fizikailag a `ctrlKey`+`shiftKey`+`H`-t figyeli, tehát a
          // token a tényleges billentyűket jelöli, nem szöveget. Lokalizálva (`Strg+…`)
          // a címke eltérne attól, amit a kód valóban kezel.
          title={
            helpdeskEnabled ? t('unifiedFab.title.withShortcut') : t('unifiedFab.title.default')
          }
        >
          <span
            className="transition-transform duration-200"
            style={{ transform: menuOpen ? 'rotate(45deg)' : 'rotate(0)' }}
          >
            {menuOpen ? '✕' : '⚡'}
          </span>

          {/* Összesített badge */}
          {!menuOpen && totalBadge > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-1 text-[10px] font-bold text-white">
              {totalBadge > 9 ? '9+' : totalBadge}
            </span>
          )}
        </button>
      </div>

      {/* CSS animation */}
      <style>{`
        @keyframes fab-item-in {
          from { opacity: 0; transform: scale(0.6) translateY(8px); }
          to { opacity: 1; transform: scale(1) translateY(0); }
        }
      `}</style>
    </>
  );
}

export const UnifiedFAB = memo(UnifiedFABInner);
